import { useQuery } from '@tanstack/react-query'
import { Check, Copy } from 'lucide-react'
import { type FormEvent, useEffect, useRef, useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAppPasswords, useCreateAppPassword, useRevokeAppPassword } from '@/features/account/hooks/use-app-passwords'
import { type CreateAppPasswordReason, type RevokeAppPasswordReason } from '@/features/auth/app-password-failure'
import { type AppPassword, type AppPasswordCredential, getMailboxInfo } from '@/features/auth/native-mailbox-api'
import { useToast } from '@/hooks/ui/use-toast'
import { cn } from '@/lib/utils'

const MAILBOX_INFO_KEY = ['native-mailbox-info'] as const

// Mirrors the auth service's description rules, so the user gets the precise
// reason inline instead of the server's generic `invalid_description`.
const MAX_DEVICE_NAME_LENGTH = 64
const RESERVED_DEVICE_NAMES = new Set(['langmail-projection', 'langmail-apple-profile'])

// What the profile's credential is called on screen. Its stored description,
// `langmail-apple-profile`, is an internal marker the user never chose.
const APPLE_PROFILE_LABEL = 'iPhone / iPad profile'

const DEVICE_NAME_ID = 'app-password-device-name'
const DEVICE_NAME_ERROR_ID = 'app-password-device-name-error'

const CREATE_ERROR: Record<CreateAppPasswordReason, string> = {
  app_password_quota: 'You have used all your app passwords. Revoke one you no longer need, then try again.',
  invalid_description: 'That device name can’t be used. Pick another.',
  rate_limited: 'Too many new passwords in a short time. Try again in a few minutes.',
  not_provisioned: 'Your mailbox is not ready yet.',
  unauthorized: 'Your session has expired. Sign in again.',
  unavailable: 'App passwords are temporarily unavailable. Try again in a moment.',
}

// Shown in the confirm dialog. `unknown_id` is not here: the password is
// already gone, so the refetch removes its row (and this dialog) and the
// outcome is told in a toast instead.
const REVOKE_ERROR: Record<Exclude<RevokeAppPasswordReason, 'unknown_id'>, string> = {
  not_provisioned: 'Your mailbox is not ready yet.',
  unauthorized: 'Your session has expired. Sign in again.',
  unavailable: 'App passwords are temporarily unavailable. Try again in a moment.',
}

const ALREADY_REVOKED = 'That password was already revoked.'
const COPY_FAILED = 'Couldn’t copy — select the field and copy it by hand.'
const COPIED_RESET_MS = 1500

// The trimmed device name, or the reason it would be refused. `takenNames` are
// the descriptions already listed: the server allows duplicates, but two rows
// with one name make it impossible to tell which device to revoke.
function parseDeviceName(
  raw: string,
  takenNames: readonly string[]
): { error: string; ok: false } | { name: string; ok: true } {
  const name = raw.trim()
  if (name.length === 0) {
    return { ok: false, error: 'Give the device a name.' }
  }
  if (name.length > MAX_DEVICE_NAME_LENGTH) {
    return { ok: false, error: `Keep the name to ${MAX_DEVICE_NAME_LENGTH} characters or fewer.` }
  }
  if (RESERVED_DEVICE_NAMES.has(name)) {
    return { ok: false, error: 'That name is reserved. Pick another.' }
  }
  const folded = name.toLowerCase()
  if (takenNames.some((taken) => taken.trim().toLowerCase() === folded)) {
    return { ok: false, error: 'You already have a password with that name. Pick another.' }
  }
  return { ok: true, name }
}

// The settings "App passwords" section: one password per mail client, so a
// lost laptop is revoked without touching the phone. Lists what exists
// (including the iPhone/iPad profile's credential), creates new ones, and
// revokes. Hidden without an active native mailbox, like `AppleProfile`.
export function AppPasswords() {
  const { data: info, isLoading } = useQuery({ queryKey: MAILBOX_INFO_KEY, queryFn: getMailboxInfo })
  const list = useAppPasswords(Boolean(info))
  const [creating, setCreating] = useState(false)

  if (isLoading || !info) {
    // No active native mailbox (or still loading) — nothing to manage.
    return null
  }

  // The hidden sync credential holds `reserved` slots, so only the rest are the
  // user's; the profile's password counts against them like any other.
  const available = list.data ? list.data.limit - list.data.reserved : null

  return (
    <div className="mb-8">
      <p className="mb-1 font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
        App passwords
      </p>
      <p className="mb-3 text-[13px] text-muted-foreground italic">
        Sign in to Apple Mail, Thunderbird or any IMAP app. Give each device its own, so you can revoke one without
        touching the rest.
      </p>

      <div className="rounded-xl border border-border bg-secondary p-4">
        {list.isLoading ? <p className="text-[13px] text-muted-foreground">Loading…</p> : null}
        {list.isError ? <p className="text-[13px] text-muted-foreground">Couldn’t load your app passwords.</p> : null}
        {list.data && list.data.passwords.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No app passwords yet.</p>
        ) : null}
        {list.data && list.data.passwords.length > 0 ? (
          <ul aria-label="App passwords" className="divide-y divide-border">
            {list.data.passwords.map((password) => (
              <AppPasswordRow key={password.id} password={password} />
            ))}
          </ul>
        ) : null}

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="font-mono text-[13px] text-muted-foreground" data-testid="app-passwords-usage">
            {list.data && available !== null ? `${list.data.passwords.length} of ${available} used` : ''}
          </span>
          <Button onClick={() => setCreating(true)} type="button" variant="outline">
            Create app password
          </Button>
        </div>
      </div>

      <CreateAppPasswordDialog
        onOpenChange={setCreating}
        open={creating}
        takenNames={list.data?.passwords.map((password) => password.description) ?? []}
      />
    </div>
  )
}

function AppPasswordRow({ password }: { password: AppPassword }) {
  const { toast } = useToast()
  const revoke = useRevokeAppPassword()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isProfile = password.kind === 'apple-profile'
  const label = isProfile ? APPLE_PROFILE_LABEL : password.description

  const handleConfirm = async () => {
    setError(null)
    const result = await revoke.mutateAsync(password.id).catch(() => ({ ok: false, reason: 'unavailable' }) as const)
    if (result.ok) {
      toast({ title: `Revoked ${label}` })
      setConfirming(false)
      return
    }
    if (result.reason === 'unknown_id') {
      // The refetch has already dropped this row, dialog and all, so an inline
      // error would never be seen.
      toast({ title: ALREADY_REVOKED })
      setConfirming(false)
      return
    }
    setError(REVOKE_ERROR[result.reason])
  }

  return (
    <li className="flex items-center justify-between gap-3 py-2 first:pt-0">
      {/* A user's own device name is shown verbatim (mono); the profile's
          credential gets its product name instead of the internal marker. */}
      <span className={cn('min-w-0 truncate text-[13px] text-foreground', !isProfile && 'font-mono')}>{label}</span>
      <Button
        aria-label={`Revoke ${label}`}
        onClick={() => setConfirming(true)}
        size="sm"
        type="button"
        variant="ghost"
      >
        Revoke
      </Button>

      <AlertDialog
        onOpenChange={(next) => {
          if (!next) {
            setError(null)
          }
          setConfirming(next)
        }}
        open={confirming}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isProfile ? 'Revoke the iPhone / iPad profile password?' : `Revoke “${password.description}”?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isProfile
                ? 'The iPhone or iPad set up with the profile stops syncing until you download the profile again.'
                : 'Any mail app signed in with this password stops syncing until you give it a new one.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error ? (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={revoke.isPending}
              onClick={(event) => {
                // Keep the dialog open until the answer is in, so a failure can
                // be shown in place.
                event.preventDefault()
                handleConfirm()
              }}
            >
              {revoke.isPending ? 'Revoking…' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

function CreateAppPasswordDialog({
  open,
  onOpenChange,
  takenNames,
}: {
  onOpenChange: (open: boolean) => void
  open: boolean
  takenNames: readonly string[]
}) {
  const create = useCreateAppPassword()
  const [deviceName, setDeviceName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [credential, setCredential] = useState<AppPasswordCredential | null>(null)

  // Reset on close so a reopened dialog never shows a stale secret.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDeviceName('')
      setError(null)
      setCredential(null)
      create.reset()
    }
    onOpenChange(next)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const parsed = parseDeviceName(deviceName, takenNames)
    if (!parsed.ok) {
      setError(parsed.error)
      return
    }
    setError(null)
    const result = await create.mutateAsync(parsed.name).catch(() => ({ ok: false, reason: 'unavailable' }) as const)
    if (result.ok) {
      setCredential(result.credential)
      return
    }
    setError(CREATE_ERROR[result.reason])
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        // The secret is shown exactly once and already holds a slot, so a stray
        // click or Escape must not throw it away: only Done or the close button
        // dismiss the reveal.
        onEscapeKeyDown={(event) => {
          if (credential) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          if (credential) {
            event.preventDefault()
          }
        }}
      >
        {credential ? (
          <>
            <DialogHeader>
              <DialogTitle>App password created</DialogTitle>
              <DialogDescription>Enter these in your mail app. IMAP and SMTP both use SSL.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <CredentialRow label="Server" value={credential.server} />
              <CredentialRow label="Username" value={credential.username} />
              <CredentialRow label="Password" value={credential.secret} />
              <div className="grid grid-cols-2 gap-3">
                <CredentialRow label="IMAP port" value={String(credential.imapPort)} />
                <CredentialRow label="SMTP port" value={String(credential.smtpPort)} />
              </div>
              <p className="text-[13px] text-muted-foreground italic">
                You won’t see this password again. Copy it now.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)} type="button">
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Create app password</DialogTitle>
              <DialogDescription>
                Name the device you’ll use it on, so you know which one to revoke later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor={DEVICE_NAME_ID}>Device name</Label>
              <Input
                aria-describedby={error ? DEVICE_NAME_ERROR_ID : undefined}
                aria-invalid={Boolean(error)}
                autoComplete="off"
                id={DEVICE_NAME_ID}
                onChange={(event) => setDeviceName(event.target.value)}
                placeholder="Laptop"
                value={deviceName}
              />
            </div>
            {error ? (
              <p className="text-destructive text-sm" id={DEVICE_NAME_ERROR_ID} role="alert">
                {error}
              </p>
            ) : null}
            <DialogFooter>
              <Button disabled={create.isPending} onClick={() => handleOpenChange(false)} type="button" variant="ghost">
                Cancel
              </Button>
              <Button disabled={create.isPending} type="submit">
                {create.isPending ? 'Creating…' : 'Create password'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

// One read-only credential line with copy-to-clipboard. Keeps the secret out of
// any logs — it only ever lives in component state and the clipboard.
function CredentialRow({ label, value }: { label: string; value: string }) {
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current)
      }
    },
    []
  )

  const copy = async () => {
    if (!(await writeToClipboard(value))) {
      toast({ title: COPY_FAILED, variant: 'destructive' })
      return
    }
    setCopied(true)
    toast({ title: `${label} copied` })
    if (resetTimer.current) {
      clearTimeout(resetTimer.current)
    }
    resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
  }

  return (
    <div>
      <p className="mb-1 font-medium font-mono text-muted-foreground text-xs uppercase tracking-widest">{label}</p>
      <div className="flex items-center gap-2">
        <Input aria-label={label} className="font-mono text-[13px]" readOnly value={value} />
        <Button aria-label={`Copy ${label.toLowerCase()}`} onClick={copy} size="icon" type="button" variant="outline">
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </Button>
      </div>
    </div>
  )
}

// False when the copy did not happen: no Clipboard API (an insecure context, an
// old browser) or a refused write (no permission, document not focused).
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
